<template>
  <h2 v-if="period && country && !dataLoaded" class="loading-placeholder">Loading...</h2>
  <b-container v-else-if="dataLoaded" fluid class="pl-0">
    <b-form-checkbox-group v-if="!bonusOnly" v-model="columnsSelected" class="small text-center my-2" id="column-filters">
      <strong class="mr-4">Show:</strong> <b-form-checkbox v-for="column, key in columns" :value="key" :key="key">{{ column.title }}</b-form-checkbox>
    </b-form-checkbox-group>
    <b-table v-if="!bonusOnly" borderless outlined class="small" small thead-tr-class="bg-secondary-mid text-white border-color-grey" sticky-header="90vh" sort-by="sort_id" :tbody-tr-class="mainRowClass" :fields="processedData.cols" :items="processedData.rows">
      <template #thead-top="data">
        <b-tr class="bg-secondary-mid text-white">
          <b-th class="border-right"></b-th>
          <b-th :class="'border-right border-bottom text-center ' + (month % 2 == 1 ? 'bg-secondary-dark' : '')" :colspan="columnsSelected.length" v-for="month in dates.months" :key="month">{{ new Date(null, month - 1).toLocaleDateString('en-GB', {month: 'long'}) }} {{ dates.year }}</b-th>
          <b-th class="border-right border-bottom text-center" colspan="3">Team count {{ dates.last_day.toLocaleDateString('en-GB')  }}</b-th>
        </b-tr>
      </template>
      <template #cell(name)="{ value, item: { type, coord_id }}">
        <div @click="toggle(coord_id)" class="zoom-in text-dark">
          <span v-if="!isAdmin">{{ value }}</span>
          <span v-else-if="type == 'c'">{{ value }} <a :href="`/rc/trainer-activity?coord_id=${coord_id}`">[activity]</a></span>
          <span v-else>∟ {{ value }}</span>
        </div>
      </template>
    </b-table>
    <h2 v-if="!bonusOnly" class="mt-4 mb-2">Bonus calculation</h2>
    <h2 v-else>From {{ dates.start.toLocaleDateString('en-GB') }} to {{ dates.last_day.toLocaleDateString('en-GB') }}</h2>
    <b-container fluid class="pl-0">
      <b-row>
        <b-col>
          <b-table borderless outlined class="small" small thead-tr-class="bg-secondary-mid text-white border-color-grey" sort-by="sort_id" :tbody-tr-class="mainRowBonusClass" :fields="bonusCalculationData.cols" :items="bonusCalculationData.rows">
            <template #cell(name)="{ value, item: { type, coord_id }}">
              <div @click="toggle(coord_id)" class="zoom-in text-dark">
                <span v-html="isAdmin && type != 'c' ? `∟ ${value}` : value"></span>
              </div>
            </template>
          </b-table>
        </b-col>
        <b-col class="small">
          <h2>Conditions</h2>
          <p><strong>Coordinators</strong> qualify for a bonus only if all their trainers qualify</p>
          <p><strong>Trainers</strong> qualify for a bonus by achieving the following per 6-month period:</p>
          <ul>
            <li>Missed modules < 25% (average per the six months)</li>
            <li>Overdue module visits < 10% (average per the six months)</li>
            <li>Overdue maintenance visits < 10% (average per the six months)</li>
            <li>PPI / modules > 1.0 (average per the six months)</li>
            <li>PPI / maintenance visits > 10 (average per the six months)</li>
            <li>Missing teacher consents = 0% (at end of period)</li>
            <li>Missing pupil consents < 20% (at end of period)</li>
            <li>Data warnings = 0 (during whole period)</li>
            <li>Geo warnings = 0 (during whole period)</li>
          </ul>
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MIOService from '../services/MIOService';
import { Chart } from 'highcharts-vue';

export default {
  name: 'RCPerformanceData',
  components: {
    Highcharts: Chart,
  },
  props: {
    country: String,
    period: String,
    bonusOnly: Boolean,
  },
  data() {
    return {
      dataLoaded: false,
      modulesRaw: [],
      observationsRaw: [],
      attendanceRaw: [],
      consentersRaw: [],
      columns: {
        missed_modules: {
          title: 'Missed modules %',
          visible: [true],
        },
        overdue_mod_visits: {
          title: 'Overdue module visits %',
          visible: [true],
        },
        overdue_mnt_visits: {
          title: 'Overdue maintenance visits %',
          visible: [true],
          includeDependingOn: 'has_mnt_visits',
        },
        ppi_per_module: {
          title: 'PPI / modules delivered',
          visible: [true],
        },
        ppi_per_mnt_visit: {
          title: 'PPI / maintenance visits',
          visible: [true],
          includeDependingOn: 'has_mnt_visits',
        },
        missing_teacher_consents: {
          title: 'Teacher consents to obtain %',
          visible: [true],
        },
        missing_student_consents: {
          title: 'Pupil consents to obtain %',
          visible: [true],
        },
        data_warnings: {
          title: 'Data warnings',
          visible: [true],
        },
        location_warnings: {
          title: 'Geo warnings',
          visible: [true],
        },
      },
      columnsSelected: [],
    };
  },
  created() {
    this.columnsSelected = Object.keys(this.columns);
    this.getData();
  },
  methods: {
    toggle(coord_id) {
      document.querySelectorAll(`.subrow:not(.coord-${coord_id})`).forEach(subrow => subrow.classList.add('hidden'));
      document.querySelectorAll(`.subrow.coord-${coord_id}`).forEach(subrow => subrow.classList.toggle('hidden'));
    },
    async getData() {
      if (!this.country || !this.period) { return; }

      const query = this.bonusOnly
        ? await MIOService.getRCBonusData(this.country, this.period)
        : await MIOService.getRCPerformanceData(this.country, this.period);

      Object.entries(query.data.results.columns).forEach(([columnId, column]) => Object.assign(this.columns[columnId], column));
      this.rcPerformanceDataRaw = this.bonusOnly ? {} : query.data.results.performanceData.rows;
      this.rcBonusDataRaw = query.data.results.bonusData.rows;
      this.dataLoaded = true;
    },
    getCurrentPeriod() {
      let today = new Date();
      return today.getFullYear() + (today.getMonth() < 6 ? 'H1' : 'H2');
    },
    mainRowClass(item, type) {
      if (!item || type !== 'row') return;
      return `border-bottom${item.type == 'c' ? '': ` subrow coord-${item.coord_id} hidden`}`;
    },
    mainRowBonusClass(item, type) {
      if (!item || type !== 'row') return;
      return item.type == 't' ? `subrow coord-${item.coord_id} hidden` : '';
    },
    mainCellClass(value, key, item) {
      if (value === '') { return '' };
      let lastUnderscorePos = key.lastIndexOf('_'),
        month = Number(key.substring(lastUnderscorePos + 1)),
        lastMonth = month - 1,
        lastMonthKey = `${key.substring(0, lastUnderscorePos)}_${lastMonth}`;
      let lastMonthNumeric = Number(item[lastMonthKey]);
      if (lastMonthNumeric == value) {
        return 'trend-equal';
      } else if (lastMonthNumeric > value) {
        return 'trend-down';
      } else if (lastMonthNumeric < value) {
        return 'trend-up';
      }
      return '';
    },
    bonusPayableClass(value, key, item) {
      if (value == 'yes') {
        return 'bg-secondary-light text-secondary-mid';
      } else if (value == 'no') {
        return 'bg-grey-light text-grey-mid';
      } else if (value == 'new') {
        return 'bg-grey-light text-info';
      }
      return '';
    },
    bonusCriteriaClass(value, key, item) {
      if (this.testBonusCondition(value, this.columns[key].bonusLevel, this.columns[key].bonusCalcTest) === false) {
        return 'bg-grey-light text-grey-mid';
      }
      return '';
    },
    testBonusCondition(amount, condition, type) {
      if (amount == '') { // Empty amount = optional field
        return true;
      }
      if (type == 'equal' && amount != condition) {
        return false;
      } else if (type == 'above' && amount < condition) {
        return false;
      } else if (type == 'below' && amount > condition) {
        return false;
      }
      return true;
    },
  },
  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
    }),
    dateOptions: {
      get() {
        let todayDate = new Date(),
          currentYear = todayDate.getFullYear(),
          currentMonth = todayDate.getMonth(),
          options = {};
        for (let y = 2020; y < currentYear; y++) {
          options[`${y}H1`] = `${y} (January – June)`;
          options[`${y}H2`] = `${y} (July – December)`;
        }
        options[`${currentYear}H1`] = `${currentYear} (January – June)`;
        if (currentMonth >= 6) {
          options[`${currentYear}H1`] = `${currentYear} (July – December)`;
        }
        return options;
      },
    },
    dates: {
      get() {
        let object,
          year = parseInt(this.period.substr(0, 4)),
          half = this.period.substr(4, 2);
        if (half == 'H1') {
          object = {
            start: new Date(`${year}-01-01`),
            start_ymd: `${year}-01-01`,
            end: new Date(`${year}-07-01`),
            end_ymd: `${year}-07-01`,
            months: [...Array(13).keys()].slice(1, 7),
          };
        } else if (half == 'H2') {
          object = {
            start: new Date(`${year}-07-01`),
            start_ymd: `${year}-07-01`,
            end: new Date(`${year+1}-01-01`),
            end_ymd: `${year+1}-01-01`,
            months: [...Array(13).keys()].slice(7, 13),
          };
        }
        object.year = year;
        object.half = half;
        let lastDay = new Date(object.end.getTime());
        lastDay.setDate(lastDay.getDate() - 1);
        object.last_day = lastDay
        return object;
      },
    },
    countries: {
      get() {
        return this.$store.state.optionsList.countries.sort();
      }
    },
    processedData: {
      get() {
        const output = { rows: this.rcPerformanceDataRaw };

        output.cols = [{
          key: 'name',
          label: 'Regional Coordinators',
          stickyColumn: true,
          class: 'border-right',
          tdClass: 'font-weight-bold text-nowrap border-bottom',
        }];

        this.dates.months.forEach(month => {
          const thClass = month % 2 == 1 ? 'bg-secondary-dark' : '';
          Object.entries(this.columns).forEach(([key, {title}]) => {
            output.cols.push({
              key: `${key}_${month}`,
              label: title,
              class: this.columnsSelected.includes(key) ? 'text-center border-right' : 'd-none',
              thClass,
              tdClass: this.mainCellClass,
            });
          });
        });

        const classNames = {
          class: 'border-right',
          tdClass: 'text-center font-weight-bold bg-secondary-light',
        };
        output.cols.push(
          { key: 'groups_after', label: 'Groups', ...classNames },
          { key: 'teachers_after', label: 'Teachers', ...classNames },
          { key: 'schools_after', label: 'Schools', ...classNames }
        );

        return output;
      }
    },
    bonusCalculationData: {
      get() {
        const output = {
          cols: [
            {
              key: 'name',
              label: 'Regional Coordinators',
              class: 'font-weight-bold text-nowrap border-right border-bottom',
            },
            {
              key: 'bonus_payable',
              label: 'Bonus payable',
              class: 'font-weight-bold text-center border-right border-bottom',
              tdClass: this.bonusPayableClass,
            },
            ...Object.entries(this.columns).map(([key, {title: label}]) => ({
              key,
              label,
              class: 'text-center border-right border-bottom',
              tdClass: this.bonusCriteriaClass,
            }))
          ],
          rows: this.rcBonusDataRaw,
        };

        return output;
      },
    },
  },
};
</script>

<style>

.table.b-table > thead > tr > .table-b-table-default {
  background-color: inherit;
  color: inherit;
}

td.trend-down:before {
  content: '↘';
  font-size: 12px;
  margin-right: 3px;
  color: #aaa;
}

td.trend-equal:before {
  content: '=';
  font-size: 12px;
  margin-right: 3px;
  color: #aaa;
}

td.trend-up:before {
  content: '↗';
  font-size: 12px;
  margin-right: 3px;
  color: #aaa;
}

.zoom-in {
  cursor: zoom-in;
}

.hidden {
  display: none;
}

</style>
